// ============================================================================
// GST ENGAGEMENT SERVICE
// ============================================================================
// Backend wraps responses in ApiResponse<T>; list endpoints wrap data in
// PagedResult<T> and are transformed to the frontend PaginatedResponse shape,
// matching every other Prima service.

import { get, post, put } from '../client/axiosClient';
import { gstEndpoints } from '../endpoints/gst.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { PaginatedResponse } from '../types';
import type {
  GstEngagement,
  UpdateGstEngagementRequest,
  AddGstEngagementRegistrationRequest,
  UpdateGstEngagementRegistrationRequest,
  WithdrawGstEngagementRegistrationRequest,
  AddGstFilingOptionRequest,
  GstFilingOptionResult,
  GstElectableQuarter,
  GstBulkCancelResult,
  GstScopeReconciliation,
  GstEngagementSearchRequest,
  CreateGstEngagementRequest,
  AvailableGstRegistration,
  GstFilingPeriod,
  GstPeriodGenerationResult,
  GstWorkflowInstallResult,
} from '../types/gstEngagement.types';

class GstEngagementService {
  async getAll(params?: GstEngagementSearchRequest): Promise<PaginatedResponse<GstEngagement>> {
    const endpoint = params
      ? buildEndpointWithQuery(gstEndpoints.engagementsBasePath(), params as Record<string, any>)
      : gstEndpoints.engagements();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const paged = raw?.data ?? {};

    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: paged.items ?? [],
      pagination: {
        currentPage: paged.page ?? 1,
        pageSize: paged.pageSize ?? 25,
        totalPages: paged.totalPages ?? 0,
        totalItems: paged.totalCount ?? 0,
        hasNextPage: paged.hasNext ?? false,
        hasPreviousPage: paged.hasPrevious ?? false,
      },
    };
  }

  /** Updates the engagement's own settings. Identity stays fixed. */
  async update(id: string, request: UpdateGstEngagementRequest): Promise<GstEngagement> {
    const response = await put<any>(gstEndpoints.engagementById(id), request);
    return response.data?.data as GstEngagement;
  }

  /**
   * Brings a GSTIN into scope mid-year and generates the periods it newly owes.
   * Generation is idempotent, so existing obligations are untouched.
   */
  async addRegistration(
    id: string, request: AddGstEngagementRegistrationRequest,
  ): Promise<GstPeriodGenerationResult> {
    const response = await post<any>(gstEndpoints.engagementRegistrations(id), request);
    return response.data?.data as GstPeriodGenerationResult;
  }

  /** Corrects a scope row's per-year settings. Cadence is elected separately. */
  async updateRegistration(
    id: string, registrationId: string, request: UpdateGstEngagementRegistrationRequest,
  ): Promise<GstEngagement> {
    const response = await put<any>(
      gstEndpoints.engagementRegistrationById(id, registrationId), request);
    return response.data?.data as GstEngagement;
  }

  /**
   * Takes a registration out of scope from a date and reports what that leaves
   * behind. Never deletes, and never touches obligations already owed.
   */
  async withdrawRegistration(
    id: string, registrationId: string, request: WithdrawGstEngagementRegistrationRequest,
  ): Promise<GstScopeReconciliation> {
    const response = await post<any>(
      gstEndpoints.engagementRegistrationWithdraw(id, registrationId), request);
    return response.data?.data as GstScopeReconciliation;
  }

  /**
   * Records a cadence election effective from a quarter. An addition, never an
   * edit — the superseded election still governs the periods it governed.
   */
  async addFilingOption(
    id: string, registrationId: string, request: AddGstFilingOptionRequest,
  ): Promise<GstFilingOptionResult> {
    const response = await post<any>(
      gstEndpoints.engagementFilingOptions(id, registrationId), request);
    return response.data?.data as GstFilingOptionResult;
  }

  /**
   * Which quarters a frequency change may start from, and why not for the rest.
   *
   * Advisory only — the POST enforces the same rule. This exists so a blocked
   * quarter can be greyed out with its reason rather than offered and refused.
   */
  async getElectableQuarters(
    id: string, registrationId: string,
  ): Promise<GstElectableQuarter[]> {
    const response = await get<any>(
      gstEndpoints.engagementElectableQuarters(id, registrationId));
    return (response.data?.data ?? []) as GstElectableQuarter[];
  }

  /** What a scope or cadence change left behind. Read-only and safe to re-poll. */
  async getScopeReconciliation(id: string): Promise<GstScopeReconciliation> {
    const response = await get<any>(gstEndpoints.engagementScopeReconciliation(id));
    return response.data?.data as GstScopeReconciliation;
  }

  /**
   * Cancels one stranded obligation. The server re-derives the reconciliation
   * rather than trusting the id, so a stale list cannot cancel something filed
   * since it was shown.
   */
  async cancelOutOfScopeObligation(
    id: string, obligationId: string, reason?: string | null,
  ): Promise<void> {
    await post<any>(gstEndpoints.engagementCancelObligation(id, obligationId), { reason });
  }

  /**
   * Cancels every superseded obligation at once.
   *
   * Sends no ids — the server re-derives what is out of scope. Filed ones are
   * never included; in-progress ones only when asked for.
   */
  async cancelSupersededObligations(
    id: string, includeInProgress = false, reason?: string | null,
  ): Promise<GstBulkCancelResult> {
    const response = await post<any>(
      gstEndpoints.engagementCancelSuperseded(id), { includeInProgress, reason });
    return response.data?.data as GstBulkCancelResult;
  }

  async getById(id: string): Promise<GstEngagement> {
    const response = await get<any>(gstEndpoints.engagementById(id));
    return response.data?.data as GstEngagement;
  }

  /**
   * Configures GST for a client and financial year. The backend originates or
   * reuses the shared Prima engagement via CreateOrGetAsync — so this may
   * attach to an engagement that already exists rather than creating one.
   */
  async create(request: CreateGstEngagementRequest): Promise<GstEngagement> {
    const response = await post<any>(gstEndpoints.engagements(), request);
    return response.data?.data as GstEngagement;
  }

  /** The engagement's filing obligations, each carrying its live EWF state. */
  async getFilingPeriods(engagementId: string): Promise<GstFilingPeriod[]> {
    const response = await get<any>(gstEndpoints.filingPeriods(engagementId));
    return (response.data?.data ?? []) as GstFilingPeriod[];
  }

  /**
   * Materialises any filing obligation this engagement owes but does not have.
   *
   * Idempotent: setup already generates the year's periods, so this is for
   * catching up after a scope or cadence change — and for engagements created
   * before generation was wired into setup.
   */
  async generateFilingPeriods(engagementId: string): Promise<GstPeriodGenerationResult> {
    const response = await post<any>(gstEndpoints.generateFilingPeriods(engagementId), {});
    return response.data?.data as GstPeriodGenerationResult;
  }

  /**
   * Recomputes every period's statutory due date from the current rules.
   *
   * For periods generated before due dates existed, and for a rule corrected
   * after the fact. Covers filed periods too — rules are effective-dated on the
   * tax period, so recomputation cannot rewrite what an earlier period was due
   * on. An applied extension is left untouched.
   */
  async recomputeDueDates(engagementId: string): Promise<GstPeriodGenerationResult> {
    const response = await post<any>(gstEndpoints.recomputeDueDates(engagementId), {});
    return response.data?.data as GstPeriodGenerationResult;
  }

  /** Definition codes this tenant is missing. Empty means generation can run. */
  async getMissingWorkflowDefinitions(): Promise<string[]> {
    const response = await get<any>(gstEndpoints.workflowDefinitionStatus());
    return (response.data?.data ?? []) as string[];
  }

  /** Imports and publishes any missing GST workflow definition. Idempotent. */
  async installWorkflowDefinitions(): Promise<GstWorkflowInstallResult> {
    const response = await post<any>(gstEndpoints.installWorkflowDefinitions(), {});
    return response.data?.data as GstWorkflowInstallResult;
  }

  async getAvailableRegistrations(
    clientId: string,
    settingsId?: string,
  ): Promise<AvailableGstRegistration[]> {
    const base = gstEndpoints.availableRegistrations(clientId);
    const endpoint = settingsId ? `${base}?settingsId=${settingsId}` : base;
    const response = await get<any>(endpoint);
    return (response.data?.data ?? []) as AvailableGstRegistration[];
  }
}

export const gstEngagementService = new GstEngagementService();
export default gstEngagementService;
